import React from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import MaskedInput from '../../Template/Input/MaskedInput';
import FormInputTooltip from '../../Template/Tooltip/FormInputTooltip';
import CompactAddFiles from '../../Template/Files/CompactAddFiles';
import currencyFormatter from '../../../utils/currencyFormatter';
import {
  createEmptyCotacaoForm,
  getCotacaoListItemKey,
  normalizeCotacaoManualEntry,
  validateCotacaoForm,
} from './cotacaoRapidaFormUtils';
import './ModalPedidoCotacao.css';

function CotacaoRapidaPropostasSection({
  cotacaoForm,
  setCotacaoForm,
  cotacoesManuais = [],
  onChangeCotacoesManuais,
  disabled = false,
  showAnexos = false,
  idPrefix = 'cotacao_rapida',
  deferralHint = 'Você também pode salvar agora e incluir propostas depois.',
}) {
  const handleAddCotacaoManual = () => {
    if (disabled) return;
    if (!validateCotacaoForm(cotacaoForm)) return;

    onChangeCotacoesManuais([
      ...cotacoesManuais,
      normalizeCotacaoManualEntry(cotacaoForm, `${Date.now()}-${cotacoesManuais.length}`),
    ]);
    setCotacaoForm(createEmptyCotacaoForm());
  };

  const handleRemoveCotacaoManual = (itemKey) => {
    if (disabled) return;
    onChangeCotacoesManuais(
      cotacoesManuais.filter((item) => String(getCotacaoListItemKey(item)) !== String(itemKey)),
    );
  };

  return (
    <Card className="border mt-2">
      <CardBody>
        <div className="d-flex align-items-start justify-content-between flex-wrap">
          <div className="mb-2 mr-3">
            <h5 className="mb-1">Já quer adicionar propostas manualmente?</h5>
            <p className="text-muted mb-0">
              Este passo é <strong>opcional</strong>. Se já tiver cotações em mãos,
              registre cada proposta com valor e descrição (vendedor, link e anexo são opcionais).
              {' '}
              {deferralHint}
            </p>
          </div>
        </div>

        <hr className="my-3" />

        <Row className="align-items-end flex-nowrap">
          <Col style={{ width: 160, flex: '0 0 160px' }}>
            <FormGroup className="mb-2">
              <div className="d-flex align-items-center mb-1">
                <Label className="font-weight-bold mb-0 text-nowrap" htmlFor={`${idPrefix}_nome_interessado`}>
                  Vendedor/prestador
                </Label>
                <FormInputTooltip text="Nome da parte interessada na compra. Exemplo: Mercado Livre, Amazon, etc" />
              </div>
              <Input
                id={`${idPrefix}_nome_interessado`}
                type="text"
                placeholder="Ex.: Mercado Livre"
                disabled={disabled}
                value={cotacaoForm.compraCotacao_nomeInteressado || ''}
                onChange={(e) => {
                  setCotacaoForm((prev) => ({
                    ...prev,
                    compraCotacao_nomeInteressado: e.target.value,
                  }));
                }}
              />
            </FormGroup>
          </Col>
          <Col style={{ width: 120, flex: '0 0 120px' }}>
            <FormGroup className="mb-2">
              <Label className="font-weight-bold mb-1 text-nowrap" htmlFor={`${idPrefix}_valor_proposta`}>
                Valor da proposta
              </Label>
              <MaskedInput
                id={`${idPrefix}_valor_proposta`}
                unmask
                mask="R$ num"
                disabled={disabled}
                blocks={{
                  num: {
                    mask: Number,
                    scale: 2,
                    signed: false,
                    thousandsSeparator: '.',
                    padFractionalZeros: false,
                    normalizeZeros: false,
                    radix: ',',
                    min: 0,
                    max: 1000000000,
                  },
                }}
                lazy={false}
                value={String(cotacaoForm.compraCotacao_vlr ?? '')}
                onAccept={(value) => {
                  if (!disabled) {
                    setCotacaoForm((prev) => ({
                      ...prev,
                      compraCotacao_vlr: value,
                    }));
                  }
                }}
              />
            </FormGroup>
          </Col>
          <Col style={{ width: 200, flex: '0 0 200px' }}>
            <FormGroup className="mb-2">
              <Label className="font-weight-bold mb-1 text-nowrap" htmlFor={`${idPrefix}_link_proposta`}>
                Link da proposta
              </Label>
              <Input
                id={`${idPrefix}_link_proposta`}
                type="url"
                placeholder="https://"
                disabled={disabled}
                value={cotacaoForm.compraCotacao_link || ''}
                onChange={(e) => {
                  setCotacaoForm((prev) => ({
                    ...prev,
                    compraCotacao_link: e.target.value,
                  }));
                }}
              />
            </FormGroup>
          </Col>
          <Col className="flex-grow-1" style={{ minWidth: 120 }}>
            <FormGroup className="mb-2">
              <Label className="font-weight-bold mb-1 text-nowrap" htmlFor={`${idPrefix}_desc_proposta`}>
                Descrição da proposta
              </Label>
              <Input
                id={`${idPrefix}_desc_proposta`}
                type="text"
                placeholder="Descrição da proposta"
                disabled={disabled}
                value={cotacaoForm.compraCotacao_desc || ''}
                onChange={(e) => {
                  setCotacaoForm((prev) => ({
                    ...prev,
                    compraCotacao_desc: e.target.value,
                  }));
                }}
              />
            </FormGroup>
          </Col>
          {showAnexos ? (
            <Col style={{ width: 160, flex: '0 0 160px' }}>
              <FormGroup className="mb-2">
                <Label className="font-weight-bold mb-1 text-nowrap">
                  Anexo
                </Label>
                <CompactAddFiles
                  multiple
                  disabled={disabled}
                  files={cotacaoForm.files || []}
                  onFilesChange={(files) => {
                    if (disabled) return;
                    setCotacaoForm((prev) => ({
                      ...prev,
                      files,
                    }));
                  }}
                />
              </FormGroup>
            </Col>
          ) : null}
          <Col xs="auto" className="flex-shrink-0">
            <FormGroup className="mb-2">
              <Button
                color="primary"
                className="btn-adicionar-proposta-pulse"
                disabled={disabled}
                onClick={handleAddCotacaoManual}
                title="Adicionar proposta"
                aria-label="Adicionar proposta"
                style={{ minWidth: 38, paddingLeft: 10, paddingRight: 10 }}
              >
                <i className="mdi mdi-plus align-middle" />
              </Button>
            </FormGroup>
          </Col>
        </Row>

        <small className="text-muted d-block mb-3">
          Preencha os campos acima e clique em <strong>+</strong>
          {' '}
          para incluí-la na lista.
          {showAnexos ? ' O anexo é opcional e será vinculado à proposta.' : null}
        </small>

        {cotacoesManuais.length > 0 ? (
          <div className="mb-0">
            <Label className="font-weight-bold mb-1">
              Propostas a salvar ({cotacoesManuais.length})
            </Label>
            <ul className="list-group">
              {cotacoesManuais.map((cotacao) => {
                const itemKey = getCotacaoListItemKey(cotacao);
                return (
                  <li
                    key={itemKey}
                    className="list-group-item d-flex justify-content-between align-items-start"
                  >
                    <div>
                      <div className="d-flex align-items-center">
                        <i className="mdi mdi-file-document-outline text-primary mr-2" />
                        <strong>
                          {cotacao.compraCotacao_nomeInteressado || 'Proposta'}
                        </strong>
                        <span className="ml-2 text-muted">
                          {currencyFormatter.format(Number(cotacao.compraCotacao_vlr) || 0)}
                        </span>
                      </div>
                      {cotacao.compraCotacao_desc ? (
                        <div className="small text-muted ml-4 pl-2">{cotacao.compraCotacao_desc}</div>
                      ) : null}
                      {cotacao.compraCotacao_link ? (
                        <div className="small ml-4 pl-2">
                          <a
                            href={
                              /^https?:\/\//i.test(String(cotacao.compraCotacao_link).trim())
                                ? String(cotacao.compraCotacao_link).trim()
                                : `https://${String(cotacao.compraCotacao_link).trim()}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Link inserido
                          </a>
                        </div>
                      ) : null}
                      {showAnexos && Array.isArray(cotacao.files) && cotacao.files.length > 0 ? (
                        <div className="small text-muted ml-4 pl-2">
                          {cotacao.files.length}
                          {' '}
                          {cotacao.files.length === 1 ? 'anexo' : 'anexos'}
                          {': '}
                          {cotacao.files
                            .map((file) => file?.props?.name || file?.data?.name || 'arquivo')
                            .join(', ')}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      disabled={disabled}
                      onClick={() => handleRemoveCotacaoManual(itemKey)}
                    >
                      Remover
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="text-muted small mb-0 d-flex align-items-center">
            <i className="mdi mdi-information-outline mr-1" />
            Nenhuma proposta adicionada ainda. Preencha os campos acima se quiser registrar propostas agora.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default CotacaoRapidaPropostasSection;
